import { Info } from 'lucide-react';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { EstablishmentNotice } from '@/lib/establishmentNoticeUtils';

interface EstablishmentNoticeAlertProps {
  notice: EstablishmentNotice | null;
  /** Affiche la case « j'ai lu » quand la note l'exige. Faux = lecture seule. */
  showAcknowledgement?: boolean;
  acknowledged?: boolean;
  onAcknowledgedChange?: (value: boolean) => void;
  compact?: boolean;
  idPrefix?: string;
}

/**
 * Note optionnelle de l'établissement affichée au client.
 * Texte brut uniquement (jamais de HTML injecté), retours à la ligne préservés.
 */
export function EstablishmentNoticeAlert({
  notice,
  showAcknowledgement = false,
  acknowledged = false,
  onAcknowledgedChange,
  compact = false,
  idPrefix = 'notice',
}: EstablishmentNoticeAlertProps) {
  if (!notice) return null;

  const needsAck = showAcknowledgement && notice.requireAcknowledgement;
  const checkboxId = `${idPrefix}-ack-${notice.id}`;

  return (
    <Alert className="bg-amber-50 border-amber-200 dark:bg-amber-950/30 dark:border-amber-800">
      <Info className="h-4 w-4 text-amber-600 dark:text-amber-400" />
      <AlertDescription
        className={`text-amber-900 dark:text-amber-200 whitespace-pre-wrap ${compact ? 'text-xs' : 'text-sm'}`}
        style={{ whiteSpace: 'pre-wrap' }}
      >
        {notice.message}
        {needsAck && (
          <div className="flex items-start space-x-2 mt-3">
            <Checkbox
              id={checkboxId}
              checked={acknowledged}
              onCheckedChange={(checked) => onAcknowledgedChange?.(checked === true)}
              className="mt-0.5"
            />
            <Label
              htmlFor={checkboxId}
              className={`leading-tight cursor-pointer font-normal ${compact ? 'text-xs' : 'text-sm'}`}
            >
              J'ai lu cette note et je l'accepte
            </Label>
          </div>
        )}
      </AlertDescription>
    </Alert>
  );
}
